import type { OpcionSelect } from "@/components";
import type { ResumenTipoUnidad, ResumenUnidades, Tipologia, TipoUnidad, Unidad } from "./api";
import { formatNumero } from "./format";
import { montoAInput, parseMonto } from "./gastos";

export const ETIQUETA_TIPO: Record<TipoUnidad, string> = { departamento: "Departamento", cochera: "Cochera", baulera: "Baulera", local: "Local", oficina: "Oficina" };
export const OPCIONES_TIPO: OpcionSelect[] = (Object.keys(ETIQUETA_TIPO) as TipoUnidad[]).map((t) => ({ valor: t, etiqueta: ETIQUETA_TIPO[t] }));
export const TODOS = "todos";
export const OPCIONES_FILTRO_TIPO: OpcionSelect[] = [{ valor: TODOS, etiqueta: "Todos los tipos" }, ...OPCIONES_TIPO];
export type FiltroVenta = "todas" | "en_venta" | "fuera_de_venta";
export const OPCIONES_FILTRO_VENTA: OpcionSelect[] = [
  { valor: "todas", etiqueta: "Todas" },
  { valor: "en_venta", etiqueta: "En venta" },
  { valor: "fuera_de_venta", etiqueta: "Fuera de venta" },
];

/** 0 → "PB", negativos → "SS1", "SS2"…, positivos → "3°". */
export function etiquetaPiso(piso: number): string {
  if (piso === 0) return "PB";
  if (piso < 0) return `SS${-piso}`;
  return `${piso}°`;
}

/** "Departamento 1° A", "Cochera SS1 12": con el piso, porque la letra del depto se repite en cada piso. */
export function nombreUnidad(u: Pick<Unidad, "tipo" | "piso" | "numero">): string {
  return `${ETIQUETA_TIPO[u.tipo]} ${etiquetaPiso(u.piso)} ${u.numero}`;
}

const PISO_MIN = -3;
const PISO_MAX = 40;

/** "Planta baja", "Subsuelo 1", "Piso 3": para leer (selector de piso, botones del edificio). */
export function nombrePiso(piso: number): string {
  if (piso === 0) return "Planta baja";
  return piso < 0 ? `Subsuelo ${-piso}` : `Piso ${piso}`;
}

/** Opciones del selector de piso (subsuelo 3 a piso 40), más el piso actual si quedó fuera de ese rango. */
export function opcionesPiso(actual: string): OpcionSelect[] {
  const pisos = Array.from({ length: PISO_MAX - PISO_MIN + 1 }, (_, i) => PISO_MIN + i);
  const n = Number(actual);
  if (actual.trim() && Number.isInteger(n) && !pisos.includes(n)) (n < PISO_MIN ? pisos.unshift(n) : pisos.push(n));
  return pisos.map((p) => ({ valor: String(p), etiqueta: nombrePiso(p) }));
}

/** Etiqueta del campo `numero`: para deptos es la letra ("A"), para el resto un número ("12"). */
export function campoNumero(tipo: TipoUnidad): { etiqueta: string; placeholder: string } {
  return tipo === "departamento" ? { etiqueta: "Depto", placeholder: "A" } : { etiqueta: "Número", placeholder: "12" };
}

// ------------------------------------------------------------------ superficies (texto, nunca float)

/** "" y "0" → "0.00"; "62,5" → "62.50"; null si no es un número ≥ 0 con hasta 2 decimales. */
export function parseSuperficie(texto: string): string | null {
  const t = texto.trim();
  if (!t || /^0+([.,]0{1,2})?$/.test(t)) return "0.00";
  const m = parseMonto(t);
  if (m === null) return null;
  const [ent = "0", dec = ""] = m.split(".");
  return `${ent}.${(dec + "00").slice(0, 2)}`;
}

/** Para el input: "0.00" → ""; "62.50" → "62,50"; "100.00" → "100". */
export function superficieAInput(valor: string): string {
  return /^0+(\.0+)?$/.test(valor) ? "" : montoAInput(valor);
}

const aCentesimos = (decimal: string): bigint => {
  const [ent = "0", dec = ""] = decimal.split(".");
  return BigInt(ent) * 100n + BigInt((dec + "00").slice(0, 2));
};
const deCentesimos = (c: bigint): string => `${c / 100n}.${String(c % 100n).padStart(2, "0")}`;

/** Total en vivo del formulario; null si alguna superficie es inválida. */
export function superficieTotal(f: Pick<FormUnidad, "sup_cubierta_m2" | "sup_semicubierta_m2" | "sup_descubierta_m2">): string | null {
  const partes = [f.sup_cubierta_m2, f.sup_semicubierta_m2, f.sup_descubierta_m2].map(parseSuperficie);
  if (partes.some((p) => p === null)) return null;
  return deCentesimos(partes.reduce((acc, p) => acc + aCentesimos(p as string), 0n));
}

export function formatM2(valor: string): string {
  return `${formatNumero(valor)} m²`;
}

/** % del fideicomiso con sus dos decimales, como la planilla: "6.18" → "6,18 %"; "14.00" → "14,00 %". */
export function formatPorcentajeUnidad(valor: string): string {
  return `${valor.replace(".", ",")} %`;
}

// ------------------------------------------------------------------ coeficientes de ponderación (texto, nunca float)

/** "0,5" → "0.500"; "1" → "1.000"; null si no es un número de 0 a 9,999 con hasta 3 decimales. */
export function parseCoeficiente(texto: string): string | null {
  const t = texto.trim().replace(",", ".");
  if (!/^\d(\.\d{1,3})?$/.test(t)) return null;
  const [ent = "0", dec = ""] = t.split(".");
  return `${ent}.${(dec + "000").slice(0, 3)}`;
}

/** Para el input y para mostrar: "1.100" → "1,1"; "1.000" → "1". */
export function coeficienteAInput(valor: string): string {
  const [ent = "0", dec = ""] = valor.split(".");
  const d = dec.replace(/0+$/, "");
  return d ? `${ent},${d}` : ent;
}

// ------------------------------------------------------------------ filtros

export interface FiltrosUnidades {
  tipo: string; // TODOS o un TipoUnidad
  venta: FiltroVenta;
}

export const filtrosIniciales = (): FiltrosUnidades => ({ tipo: TODOS, venta: "todas" });

export function queryUnidades(f: FiltrosUnidades): string {
  const p = new URLSearchParams();
  if (f.tipo !== TODOS) p.set("tipo", f.tipo);
  if (f.venta === "en_venta") p.set("fuera_de_venta", "false");
  if (f.venta === "fuera_de_venta") p.set("fuera_de_venta", "true");
  const q = p.toString();
  return q ? `?${q}` : "";
}

// ------------------------------------------------------------------ formulario

export interface FormUnidad {
  numero: string;
  piso: string;
  tipo: TipoUnidad;
  sup_cubierta_m2: string;
  sup_semicubierta_m2: string;
  sup_descubierta_m2: string;
  ambientes: string;
  orientacion: string;
  fuera_de_venta: boolean;
  motivo_fuera_de_venta: string;
  notas: string;
}

export type ErroresUnidad = Partial<Record<keyof FormUnidad, string>>;

export interface PayloadUnidad {
  numero: string;
  piso: number;
  tipo: TipoUnidad;
  sup_cubierta_m2: string;
  sup_semicubierta_m2: string;
  sup_descubierta_m2: string;
  ambientes: number | null;
  orientacion: string | null;
  fuera_de_venta: boolean;
  motivo_fuera_de_venta: string | null;
  notas: string | null;
}

export function formUnidad(u?: Unidad, opciones: { duplicar?: boolean } = {}): FormUnidad {
  if (!u) {
    return { numero: "", piso: "", tipo: "departamento", sup_cubierta_m2: "", sup_semicubierta_m2: "", sup_descubierta_m2: "", ambientes: "", orientacion: "", fuera_de_venta: false, motivo_fuera_de_venta: "", notas: "" };
  }
  return {
    numero: opciones.duplicar ? "" : u.numero,
    piso: String(u.piso),
    tipo: u.tipo,
    sup_cubierta_m2: superficieAInput(u.sup_cubierta_m2),
    sup_semicubierta_m2: superficieAInput(u.sup_semicubierta_m2),
    sup_descubierta_m2: superficieAInput(u.sup_descubierta_m2),
    ambientes: u.ambientes === null ? "" : String(u.ambientes),
    orientacion: u.orientacion ?? "",
    fuera_de_venta: u.fuera_de_venta,
    motivo_fuera_de_venta: u.motivo_fuera_de_venta ?? "",
    notas: u.notas ?? "",
  };
}

const PISO = /^-?\d{1,3}$/;

export function validarUnidad(f: FormUnidad): ErroresUnidad {
  const e: ErroresUnidad = {};
  const numero = f.numero.trim();
  if (!numero) e.numero = f.tipo === "departamento" ? "Ingresá el depto (ej. A)" : "Ingresá el número de la unidad";
  else if (numero.length > 20) e.numero = "Hasta 20 caracteres";
  if (!PISO.test(f.piso.trim()) || Number(f.piso) < -10 || Number(f.piso) > 200) e.piso = "Elegí el piso";
  for (const campo of ["sup_cubierta_m2", "sup_semicubierta_m2", "sup_descubierta_m2"] as const) {
    if (parseSuperficie(f[campo]) === null) e[campo] = "Ingresá los m² (hasta 2 decimales)";
  }
  if (f.ambientes.trim() && !/^\d{1,2}$/.test(f.ambientes.trim())) e.ambientes = "Ingresá un número entero";
  if (f.orientacion.trim().length > 40) e.orientacion = "Hasta 40 caracteres";
  if (f.fuera_de_venta && !f.motivo_fuera_de_venta.trim()) e.motivo_fuera_de_venta = "Indicá el motivo";
  else if (f.motivo_fuera_de_venta.trim().length > 200) e.motivo_fuera_de_venta = "Hasta 200 caracteres";
  if (f.notas.trim().length > 2000) e.notas = "Hasta 2000 caracteres";
  return e;
}

export function aPayloadUnidad(f: FormUnidad): PayloadUnidad {
  return {
    numero: f.numero.trim(),
    piso: Number(f.piso),
    tipo: f.tipo,
    sup_cubierta_m2: parseSuperficie(f.sup_cubierta_m2) ?? "0.00",
    sup_semicubierta_m2: parseSuperficie(f.sup_semicubierta_m2) ?? "0.00",
    sup_descubierta_m2: parseSuperficie(f.sup_descubierta_m2) ?? "0.00",
    ambientes: f.ambientes.trim() ? Number(f.ambientes) : null,
    orientacion: f.orientacion.trim() || null,
    fuera_de_venta: f.fuera_de_venta,
    motivo_fuera_de_venta: f.fuera_de_venta ? f.motivo_fuera_de_venta.trim() || null : null,
    notas: f.notas.trim() || null,
  };
}

// ------------------------------------------------------------------ tarjeta de Inicio y encabezado

/** El Inicio muestra la tarjeta de unidades recién con la primera cargada. */
export function hayUnidades(r: ResumenUnidades): boolean {
  return r.total.cantidad > 0;
}

/** "2 disponibles · 0 reservadas · 1 vendidas", más " · 1 adjudicadas" si hay alguna adjudicada a un clase B. */
export function detalleResumen(t: ResumenTipoUnidad): string {
  const adjudicadas = t.adjudicadas > 0 ? ` · ${t.adjudicadas} adjudicadas` : "";
  return `${t.disponibles} disponibles · ${t.reservadas} reservadas · ${t.vendidas} vendidas${adjudicadas}`;
}

export function tarjetaUnidades(r: ResumenUnidades): { valor: string; detalle: string } {
  return { valor: String(r.total.cantidad), detalle: detalleResumen(r.total) };
}

// ------------------------------------------------------------------ tipologías

/** Las que tienen renders y pueden llevar tipología: las mismas del recorrido (`CON_FACHADA` del backend). */
export const TIPOS_CON_TIPOLOGIA: readonly TipoUnidad[] = ["departamento", "local", "oficina"];

/** Opciones del `Select` "Tipología": cada una y, si la unidad ya tiene, al principio "Sin tipología" (valor "") para sacársela
 * (sin tipología, el campo queda vacío para escribir el nombre de una nueva). */
export function opcionesTipologia(tipologias: Tipologia[], tieneTipologia: boolean): OpcionSelect[] {
  const opciones = tipologias.map((t) => ({ valor: String(t.id), etiqueta: t.nombre }));
  return tieneTipologia ? [{ valor: "", etiqueta: "Sin tipología" }, ...opciones] : opciones;
}

/** "2° A, 3° A": con quiénes comparte los renders (las otras unidades de la tipología); "" si está sola. */
export function compartidaCon(t: Tipologia, unidadId: number): string {
  return t.unidades
    .filter((u) => u.id !== unidadId)
    .map((u) => `${etiquetaPiso(u.piso)} ${u.numero}`)
    .join(", ");
}
